import { Brain, Loader2, CheckCircle2, Zap, UserRound, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage } from './types';

// Markdown component overrides — keeps rendered output compact inside the
// chat bubble (no jarring h1 jumps, lists indent cleanly, links underline).
// Mirrors platform-sdk's ChatMessageBody (which is the proven recipe used by
// chanl-admin's inbox chat).
const MD_COMPONENTS = {
  p: (props: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p {...props} className="my-1.5 leading-relaxed first:mt-0 last:mb-0" />
  ),
  strong: (props: React.HTMLAttributes<HTMLElement>) => (
    <strong {...props} className="font-semibold" />
  ),
  em: (props: React.HTMLAttributes<HTMLElement>) => <em {...props} className="italic" />,
  ul: (props: React.HTMLAttributes<HTMLUListElement>) => (
    <ul {...props} className="ml-4 my-1.5 list-disc space-y-0.5 marker:text-current/70" />
  ),
  ol: (props: React.HTMLAttributes<HTMLOListElement>) => (
    <ol {...props} className="ml-4 my-1.5 list-decimal space-y-0.5 marker:text-current/70" />
  ),
  li: (props: React.HTMLAttributes<HTMLLIElement>) => (
    <li {...props} className="leading-relaxed" />
  ),
  h1: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <div {...props} className="font-semibold text-sm mt-2 mb-1 first:mt-0" />
  ),
  h2: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <div {...props} className="font-semibold text-sm mt-2 mb-1 first:mt-0" />
  ),
  h3: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <div {...props} className="font-medium text-sm mt-1.5 mb-0.5 first:mt-0" />
  ),
  blockquote: (props: React.HTMLAttributes<HTMLQuoteElement>) => (
    <blockquote
      {...props}
      className="my-1.5 border-l-2 border-current/20 pl-3 italic opacity-90"
    />
  ),
  code: (props: React.HTMLAttributes<HTMLElement>) => (
    <code
      {...props}
      className="rounded bg-black/10 dark:bg-white/10 px-1 py-0.5 font-mono text-[0.9em]"
    />
  ),
  pre: (props: React.HTMLAttributes<HTMLPreElement>) => (
    <pre {...props} className="my-1.5 overflow-x-auto rounded p-2 text-xs" />
  ),
  a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    // eslint-disable-next-line jsx-a11y/anchor-has-content -- href passed via spread
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      className="underline underline-offset-2 opacity-90 hover:opacity-100"
    />
  ),
  hr: () => <hr className="my-2 border-current/10" />,
  table: (props: React.HTMLAttributes<HTMLTableElement>) => (
    <div className="my-1.5 overflow-x-auto">
      <table {...props} className="w-full text-left text-xs border-collapse" />
    </div>
  ),
  th: (props: React.HTMLAttributes<HTMLTableCellElement>) => (
    <th {...props} className="border border-current/10 px-2 py-1 font-semibold" />
  ),
  td: (props: React.HTMLAttributes<HTMLTableCellElement>) => (
    <td {...props} className="border border-current/10 px-2 py-1" />
  ),
};

interface ChatMessageProps {
  message: ChatMessage;
}

/** Status pill — friendly "Searching listings..." with spinner */
function StatusPill({ message }: { message: ChatMessage }) {
  const label = message.label ?? message.content;
  const isDone = label.startsWith('✓') || message.duration != null;

  return (
    <div className="flex justify-start">
      <div className="flex w-fit items-center gap-2 rounded-full bg-muted/50 px-3 py-1.5 dark:bg-white/[0.06]">
        {isDone ? (
          <CheckCircle2 className="size-3.5 shrink-0 text-emerald-500" />
        ) : (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground dark:text-white/50" />
        )}
        <p className="text-xs text-muted-foreground dark:text-white/50">
          {label}
        </p>
        {message.duration != null && (
          <span className="text-[10px] text-muted-foreground/60 dark:text-white/30">
            {message.duration.toFixed(1)}s
          </span>
        )}
      </div>
    </div>
  );
}

/** Thought badge — "⚡ Thought for 2.3s · Used 1 tool" */
function ThoughtBadge({ message }: { message: ChatMessage }) {
  return (
    <div className="flex justify-start">
      <div className="flex w-fit items-center gap-1.5 rounded-full bg-muted/40 px-2.5 py-1 dark:bg-white/[0.04]">
        <Zap className="size-3 text-muted-foreground/50 dark:text-white/30" />
        <p className="text-[10px] text-muted-foreground/60 dark:text-white/25">
          {message.content}
        </p>
      </div>
    </div>
  );
}

export function ChatMessageBubble({ message }: ChatMessageProps) {
  // Status pill (friendly tool/action label)
  if (message.role === 'status') {
    return <StatusPill message={message} />;
  }

  // Thinking indicator
  if (message.role === 'thinking') {
    return (
      <div className="flex justify-start">
        <div className="flex w-fit items-center gap-2 rounded-full bg-muted/50 px-3 py-1.5 dark:bg-white/[0.06]">
          <Brain className="size-3.5 shrink-0 animate-pulse text-primary" />
          <p className="text-xs text-muted-foreground dark:text-white/50">
            {message.label ?? message.content}
          </p>
        </div>
      </div>
    );
  }

  // Collapsed tool badge (after response)
  if (message.role === 'tool') {
    return <ThoughtBadge message={message} />;
  }

  // Human-handoff note — a person has taken over the thread, so the AI is
  // suppressed. Shown instead of an empty/JSON bubble (Intercom pattern).
  if (message.role === 'handoff') {
    return (
      <div className="flex justify-center py-1" data-testid="handoff-note">
        <div className="flex max-w-[90%] items-center gap-2 rounded-full bg-muted/60 px-3.5 py-1.5 dark:bg-white/[0.06]">
          <UserRound className="size-3.5 shrink-0 text-muted-foreground dark:text-white/50" />
          <p className="text-xs text-muted-foreground dark:text-white/60">{message.content}</p>
        </div>
      </div>
    );
  }

  // In-band stream error — surfaced as a friendly message, never raw protocol.
  if (message.role === 'error') {
    return (
      <div className="flex justify-start" data-testid="error-note">
        <div className="flex w-fit max-w-[85%] items-start gap-2 rounded-2xl rounded-bl-sm bg-destructive/10 px-4 py-3 text-destructive dark:bg-destructive/20">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <p className="text-sm leading-relaxed">{message.content}</p>
        </div>
      </div>
    );
  }

  // User message
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-foreground px-4 py-3 dark:bg-primary/80">
          <p className="text-sm leading-relaxed text-background dark:text-primary-foreground">
            {message.content}
          </p>
        </div>
      </div>
    );
  }

  // Assistant message — white bg in light, semi-transparent in dark.
  // Renders markdown (bold/italic/lists/links/headings/inline code) via
  // react-markdown + remark-gfm, matching platform-sdk's ChatMessageBody.
  // The bubble container drives the base text color; markdown components
  // inherit it via `text-card-foreground dark:text-white/90` on the outer
  // div, so links/code/strong all read correctly in both themes.
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-card px-4 py-3 shadow-sm dark:bg-white/[0.08] dark:shadow-none">
        <div className="text-sm leading-relaxed text-card-foreground wrap-break-word dark:text-white/90 [&_p]:wrap-break-word">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
            {message.content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
